import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useParams, useRouteMatch } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton } from '@material-ui/core';
import GridContainer from '../../../components/Grid/GridContainer';
import { AddBoxIcon, IndeterminateCheckBoxIcon } from '../../../components/Icons/Icon';
import GridItem from '../../../components/Grid/GridItem';
import Button from '../../../components/Button/CustomButton';
import Typography from '../../../components/Typography/CustomTypography';
import CleanableInput from '../../../components/Input/CleanableInput';
import styles from '../../../assets/jss/pages/QAListManagermentStyle';
import Loader from '../../../components/Loader/Loader';
import Validation from '../../../common/Validation';
import AlertDialog from '../../../components/Dialog/AlertDialog';
import Snackbar from '../../../components/Snackbar/Snackbar';
import Tabs from '../../../components/Tab/Tab';

const useStyles = makeStyles(styles);

const QA = (props) => {
    const {
        index,
        handleUpdateQA,
        handleDeleteQA,
        handleAddQA,
        dataQA,
        arrData,
        setQAsArr,
        currentTab,
    } = props;
    const classes = useStyles();
    const [params, setParams] = useState({});
    const [validateResults, setValidateResults] = useState({});
    const intl = useIntl();

    useEffect(() => {
        setParams(dataQA);
    }, [dataQA]);

    useEffect(() => {
        arrData[index] = { ...params };
    }, [params]);

    const handleCreateUpdate = () => {
        if (validate()) {
            if (params.id) {
                handleUpdateQA(params);
            } else {
                handleAddQA(params, index);
            }
        }
    };

    const handleSetParams = useCallback((field, valueChange) => {
        setParams((prevParams) => ({ ...prevParams, [field]: valueChange }));
    }, []);

    const validate = () => {
        const {
            answer, question,
        } = params;

        const validateRules = {
            answer: [() => Validation.isNotEmpty(answer)],
            question: [() => Validation.isNotEmpty(question)],
        };
        const validateResultEN = {
            answer: [
                () => Validation.isNotEmpty(answer),
                () => Validation.validateLatin(answer),
            ],
            question: [
                () => Validation.isNotEmpty(question),
                () => Validation.validateLatin(question),
            ],
        };
        const validateResult = Validation.validateAllRules((currentTab === 3 || currentTab === 2) ? validateResultEN : validateRules);
        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            window.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    const deleteQA = () => {
        if (params.id) {
            handleDeleteQA(params.id);
        } else {
            arrData.splice(index, 1);
            setQAsArr([...arrData]);
        }
    };

    return (
        <GridContainer>
            <GridItem className={classes.main}>
                <fieldset className={classes.fieldset}>
                    <legend>
                        <Typography variant="h5" color="primary" bold>{`設問 ${index + 1}`}</Typography>
                    </legend>
                    <IconButton size="small" classes={{ root: classes.advertisementRemoveButton }} onClick={deleteQA}>
                        <IndeterminateCheckBoxIcon size="40px" />
                    </IconButton>
                    <GridContainer style={{ margin: '15px 0' }}>
                        <GridItem md={2} xs={12}>
                            <Typography required bold>
                                質問
                            </Typography>
                        </GridItem>
                        <GridItem md={10} xs={12}>
                            <CleanableInput
                                isMultiline
                                defaultValue={params.question}
                                onChange={(valueInput) => handleSetParams('question', valueInput)}
                                error={!!validateResults.question}
                                helperText={validateResults.question
                                    ? intl.formatMessage({ id: validateResults.question.message }) : ''}
                            />
                        </GridItem>
                    </GridContainer>
                    <GridContainer style={{ margin: '15px 0' }}>
                        <GridItem md={2} xs={12}>
                            <Typography required bold>
                                回答
                            </Typography>
                        </GridItem>
                        <GridItem md={10} xs={12}>
                            <CleanableInput
                                isMultiline
                                defaultValue={params.answer}
                                onChange={(valueInput) => handleSetParams('answer', valueInput)}
                                error={!!validateResults.answer}
                                helperText={validateResults.answer
                                    ? intl.formatMessage({ id: validateResults.answer.message }) : ''}
                            />
                        </GridItem>
                    </GridContainer>
                    <GridItem className={classes.button}>
                        <Button
                            size="md"
                            color="primary"
                            onClick={handleCreateUpdate}
                        >
                            <FormattedMessage id="apply" />
                        </Button>
                    </GridItem>
                </fieldset>
            </GridItem>
        </GridContainer>
    );
};

QA.defaultProps = {
    index: 1,
    dataQA: {},
    arrData: [],
};

QA.propTypes = {
    handleUpdateQA: PropTypes.func.isRequired,
    handleDeleteQA: PropTypes.func.isRequired,
    handleAddQA: PropTypes.func.isRequired,
    index: PropTypes.number,
    dataQA: PropTypes.oneOfType([() => null, PropTypes.object]),
    arrData: PropTypes.arrayOf(PropTypes.object),
    setQAsArr: PropTypes.func.isRequired,
    currentTab: PropTypes.number.isRequired,
};

const QAListManagerment = (props) => {
    const {
        fetchListQA, listQA, createQA, updatQA, deleteQA, createSuccess, updateSuccess, deleteSuccess, isFetching,
        clear, errorsQA, eventInfo,
    } = props;
    const classes = useStyles();
    const [qAsArr, setQAsArr] = useState([]);
    const [isOpenDialogDelete, setIsOpenDialogDelete] = useState(false);
    const [idDelete, setIdDelete] = useState(false);
    const useParam = useParams();
    const { shortName } = useParam;
    const [snackbar, setSnackbar] = useState({ isOpen: false, type: 'success', message: '' });
    const [currentTab, setCurrentTab] = useState(0);
    useEffect(() => {
        fetchListQA({
            qa_type: currentTab,
            event_name: shortName,
            language: currentTab > 1 ? 'en' : 'ja',
        });
        return () => {
            setIsOpenDialogDelete(false);
            setSnackbar({ isOpen: false, type: 'success', message: '' });
            clear();
        };
    }, [currentTab]);

    useEffect(() => {
        if (listQA) {
            setQAsArr(listQA);
        }
    }, [listQA]);

    useEffect(() => {
        if (updateSuccess) {
            setSnackbar({
                ...snackbar,
                isOpen: true,
                type: 'success',
                message: <FormattedMessage id="apiSuccess.update" />,
            });
        }
    }, [updateSuccess]);

    useEffect(() => {
        if (createSuccess) {
            setSnackbar({
                ...snackbar,
                isOpen: true,
                type: 'success',
                message: <FormattedMessage id="apiSuccess.create" />,
            });
        }
    }, [createSuccess]);

    useEffect(() => {
        setIsOpenDialogDelete(false);
        if (deleteSuccess) {
            setSnackbar({
                ...snackbar,
                isOpen: true,
                type: 'success',
                message: <FormattedMessage id="apiSuccess.delete" />,
            });
        }
    }, [deleteSuccess]);

    useEffect(() => {
        if (!_.isEmpty(errorsQA)) {
            setIsOpenDialogDelete(false);
            setSnackbar({
                ...snackbar,
                isOpen: true,
                type: 'error',
                message: errorsQA?.message?.message,
            });
            clear();
            fetchListQA({
                qa_type: currentTab,
                event_name: shortName,
            });
        }
    }, [errorsQA]);

    const handleAddItemQA = () => {
        setQAsArr([...qAsArr, { answer: '', question: '' }]);
    };

    const handleAddQA = (data, index) => {
        createQA({
            question: data.question,
            answer: data.answer,
            qa_type: currentTab,
            event_name: shortName,
            curentList: qAsArr,
            index,
            language: currentTab > 1 ? 'en' : 'ja',
        });
    };

    const handleDelete = (id) => {
        setIsOpenDialogDelete(true);
        setIdDelete(id);
    };
    const handleUpdate = (data) => {
        updatQA({
            qa_id: data.id,
            question: data.question,
            answer: data.answer,
            qa_type: currentTab,
            event_name: shortName,
            curentList: qAsArr,
            language: currentTab > 1 ? 'en' : 'ja',
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, isOpen: false });
    };

    const dialogConfirmDelete = () => {
        deleteQA({
            qa_id: idDelete,
            event_name: shortName,
            curentList: qAsArr,
        });
    };
    const handleChangeTab = (val) => {
        setCurrentTab(val);
    };

    return (
        <>
            <Tabs
                headers={_.isEmpty(eventInfo.languages) ? [
                    <FormattedMessage id="adminSidebar.qaViewerManagement" />,
                    <FormattedMessage id="adminSidebar.qaOrganizerManagement" />,
                ] : [
                    <FormattedMessage id="adminSidebar.qaViewerManagement" />,
                    <FormattedMessage id="adminSidebar.qaOrganizerManagement" />,
                    <FormattedMessage id="adminSidebar.qaViewerManagementEN" />,
                    <FormattedMessage id="adminSidebar.qaOrganizerManagementEN" />,
                ]}
                onChangeTab={handleChangeTab}
            />
            <GridContainer style={{ justifyContent: 'center', marginTop: '20px' }}>
                <GridItem md={10}>
                    {qAsArr.length > 0 ? qAsArr.map((item, index) => (
                        <QA
                            index={index}
                            dataQA={item}
                            handleUpdateQA={handleUpdate}
                            handleDeleteQA={handleDelete}
                            handleAddQA={handleAddQA}
                            arrData={qAsArr}
                            setQAsArr={setQAsArr}
                            key={Number(index)}
                            currentTab={currentTab}
                        />
                    ))
                        : (
                            <Typography align="center" variant="h5">
                                QAはまだ作成されていません
                            </Typography>
                        )}
                </GridItem>
                <GridItem md={10}>
                    <IconButton classes={{ root: classes.addButton }} onClick={handleAddItemQA}>
                        <AddBoxIcon size="40px" />
                    </IconButton>
                </GridItem>
            </GridContainer>
            <AlertDialog
                title={<FormattedMessage id="dialog.confirmDeleteTitle" />}
                handleClose={() => setIsOpenDialogDelete(false)}
                handleOpen={isOpenDialogDelete}
                handleSubmit={dialogConfirmDelete}
                contents="削除しますか？"
            />
            <Snackbar
                open={snackbar.isOpen}
                onClose={handleCloseSnackbar}
                message={snackbar.message}
                severity={snackbar.type}
            />
            <Loader isLoading={isFetching} />
        </>
    );
};

QAListManagerment.defaultProps = {
    listQA: [],
    createSuccess: false,
    updateSuccess: false,
    deleteSuccess: false,
    isFetching: false,
    errorsQA: {},
    eventInfo: {},
};

QAListManagerment.propTypes = {
    listQA: PropTypes.oneOfType([() => null, PropTypes.array]),
    fetchListQA: PropTypes.func.isRequired,
    createQA: PropTypes.func.isRequired,
    updatQA: PropTypes.func.isRequired,
    deleteQA: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
    createSuccess: PropTypes.bool,
    updateSuccess: PropTypes.bool,
    deleteSuccess: PropTypes.bool,
    isFetching: PropTypes.bool,
    errorsQA: PropTypes.oneOfType([() => null, PropTypes.object]),
    eventInfo: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.bool,
        PropTypes.string,
        PropTypes.number,
    ])),
};

export default QAListManagerment;
